/**
 * @file ScrollControls.jsx
 * @description Always-on UX controls that sit above every page:
 *   1. A scroll-progress bar pinned to the very top of the viewport — gives
 *      the user instant feedback on how far through the page they are.
 *   2. A persistent floating "Register" button — the primary action is never
 *      more than one tap away, on any page, at any scroll position.
 *   3. A "Back to top" button that fades in once the user scrolls down.
 *
 *   Mounted once, globally, inside the Router (see src/index.js).
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import '../ComponentsStyling/ScrollControls.css';

const ScrollControls = () => {
    const [scrolled, setScrolled] = useState(false);
    const fillRef = useRef(null);
    const barRef = useRef(null);

    useEffect(() => {
        let raf = 0;
        const update = () => {
            raf = 0;
            const doc = document.documentElement;
            const max = doc.scrollHeight - doc.clientHeight;
            const y = window.scrollY || doc.scrollTop || 0;
            const p = max > 0 ? Math.min(1, y / max) : 0;
            /* Drive the bar straight on the compositor via ref — no per-frame
               React re-render, so scrolling stays smooth on every device. */
            if (fillRef.current) fillRef.current.style.transform = `scaleX(${p})`;
            if (barRef.current) barRef.current.setAttribute('aria-valuenow', String(Math.round(p * 100)));
            setScrolled(y > 420); // only re-renders when crossing the threshold
        };
        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(update);
        };
        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    const toTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <>
            {/* Reading-progress bar */}
            <div className="ux-progress" ref={barRef} role="progressbar" aria-label="Page scroll progress"
                aria-valuenow={0} aria-valuemin={0} aria-valuemax={100}>
                <span className="ux-progress__fill" ref={fillRef} />
            </div>

            {/* Floating action buttons */}
            <div className="ux-fabs">
                <button
                    type="button"
                    className={`ux-fab ux-fab--top${scrolled ? ' ux-fab--show' : ''}`}
                    onClick={toTop}
                    aria-label="Back to top"
                    tabIndex={scrolled ? 0 : -1}
                >
                    <svg viewBox="0 0 18 18" fill="none" aria-hidden>
                        <path d="M9 15V4M9 4L4 9M9 4l5 5" stroke="currentColor"
                            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        </>
    );
};

export default ScrollControls;
